import { useCallback, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ApiError } from "../../../lib/http";
import { useTokenGetter } from "../../auth/hooks/useTokenGetter";
import {
  fetchBillingProfile,
  saveBillingProfile,
} from "../services/billingProfileService";
import type { BillingDetails } from "../types/billing.types";

export type BillingProfileStatus = "loading" | "ready" | "failed";

export interface BillingProfileState {
  /** Who this workspace is invoiced, or `null` for one that has never said. */
  profile: BillingDetails | null;
  status: BillingProfileStatus;
  error?: string;
  reload: () => void;
  /** Records who to invoice and answers with what was stored. Throws where the
   *  write was refused, so the screen can say what the server said. */
  save: (details: BillingDetails) => Promise<BillingDetails>;
}

export const BILLING_PROFILE_QUERY_KEY = ["billing", "profile"] as const;

/**
 * The name and address Naurist's invoice is headed with.
 *
 * `null` and `ready` for a workspace that has never filled the form in — not a
 * failure, and not a screen to draw differently: the checkout opens on what it
 * can gather from `My brand` instead, which is what it always did.
 *
 * A failure *is* drawn, though, and not fatally: these are the buyer's own
 * details and the form works perfectly well typed into by hand, so the checkout
 * says why it is empty rather than blocking the purchase. See the notice above
 * `BillingDetailsForm`.
 *
 * Backed by TanStack Query: a screen visited again shows the last-saved
 * address immediately while a fresh read checks it behind the scenes, rather
 * than making the checkout wait on the network every time it opens.
 *
 * Must be used inside a Clerk provider and an active session.
 */
export function useBillingProfile(): BillingProfileState {
  const getToken = useTokenGetter();
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: BILLING_PROFILE_QUERY_KEY,
    queryFn: ({ signal }) => fetchBillingProfile(getToken, signal),
  });

  useEffect(() => {
    if (query.isError) {
      console.error("billing profile failed to load:", query.error);
    }
  }, [query.isError, query.error]);

  /**
   * Writing it back.
   *
   * What the server answers with goes straight into the cache — it trims, it
   * upper-cases a GSTIN, and the form should end up holding what the invoice
   * will actually carry rather than what was typed at it.
   *
   * It throws rather than swallowing: this runs on the way to a payment, and a
   * purchase must not go ahead on the quiet assumption that the invoice details
   * were recorded.
   */
  const save = useCallback(
    async (details: BillingDetails) => {
      const stored = await saveBillingProfile(getToken, details);
      queryClient.setQueryData<BillingDetails>(BILLING_PROFILE_QUERY_KEY, stored);
      return stored;
    },
    [getToken, queryClient]
  );

  return {
    profile: query.data ?? null,
    status:
      query.status === "pending"
        ? "loading"
        : query.status === "error"
        ? "failed"
        : "ready",
    error:
      query.isError && query.error instanceof ApiError
        ? query.error.message
        : query.isError
        ? "We could not reach the server. Check your connection and try again."
        : undefined,
    reload: () => {
      void query.refetch();
    },
    save,
  };
}

export default useBillingProfile;
